import { Typography, useEventCallback } from '@mui/material';
import FilterAct from 'civil-status/components/research/filter';
import TableAct from 'civil-status/components/research/table';
import { ActDraftResearchCrumbs } from 'civil-status/constants/breadcrumb';
import { transformerActList } from 'civil-status/utils/transformer-act-list';
import { ActListVM, ResearchActForm } from 'civil-status/view-models/research-form';
import { Form, Formik } from 'formik';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ActBozza } from 'shell/models/act-list';
import { HttpResponseList } from 'shell/models/http-response';
import { Operation } from 'shell/view-models/operation';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';
import * as yup from 'yup';

const validationSchema = yup.object({
  dateEnd: yup.date().nullable().typeError('Data non valida'),
  dateStart: yup
    .date()
    .nullable()
    .typeError('Data non valida')
    .when('dateEnd', (dateEnd, formvalidationSchema) =>
      dateEnd && dateEnd?.toString() !== 'Invalid Date'
        ? formvalidationSchema.min(dateEnd, 'La data deve essere minore della Data fine validità')
        : formvalidationSchema.nullable()
    ),
});

const ActResearchPage = (): ReactElement => {
  const [openFilter, setOpenFilter] = useState<boolean>(true);
  const [filterSelected, setFilterSelected] = useState<OperationObject>({});
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const filterTable = useFilterTable<ResearchActForm>();

  const [actList, getActList] = useHttpFetch<HttpResponseList<ActBozza>, Array<ActListVM>>(
    `${__ACT_API_URL__}/bozza/ricerca`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformerActList,
    }
  );

  const getActListData = useEventCallback(
    (dataFilter: OperationObject, page?: number, rowsPerPage?: number) => {
      const queryParams = `${paginationListToQueryString({
        filters: dataFilter,
        orders: {},
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      })}`;

      (
        getActList({
          params: {},
          paramsSerializer: () => queryParams,
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(dataFilter);
      });
    }
  );

  const handleSubmit = useEventCallback((values: ResearchActForm) => {
    // mantenere dati
    filterTable.setValues(values);

    const dataFilter: OperationObject = {
      codiceStato: {
        value: values?.stateAct || '',
        operation: Operation.EQUALS,
      },
      codiceOperatore: {
        value: values?.codOperator || '',
        operation: Operation.EQUALS,
      },
      tipoEvento: {
        value: values?.typeEvent || '',
        operation: Operation.EQUALS,
      },
      tipoContenuto: {
        value: values?.typeContent || '',
        operation: Operation.EQUALS,
      },
      dataFormazioneAttoDa: {
        value: values?.dataStart ? values?.dataStart.toFormat('yyyy-MM-dd') : '',
        operation: Operation.EQUALS,
      },
      dataFormazioneAttoA: {
        value: values?.dataEnd ? values?.dataEnd.toFormat('yyyy-MM-dd') : '',
        operation: Operation.EQUALS,
      },
    };

    setTableProps({
      page: 0,
      rowsPerPage: 10,
    });

    getActListData(dataFilter);
  });

  useEffect(() => {
    if (((actList.data as Array<ActListVM>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpenFilter(false);
    }
  }, [actList.data]);

  return (
    <Fragment>
      <Breadcrumbs items={ActDraftResearchCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Ricerca atto da completare
        </Typography>
      </div>
      <div className="circle" />
      <Formik
        initialValues={
          filterTable.values ||
          ({
            stateAct: '',
            codOperator: '',
            typeEvent: '',
          } as ResearchActForm)
        }
        onSubmit={handleSubmit}
        validateOnMount
        validationSchema={validationSchema}
      >
        <Form>
          <FilterAct open={openFilter} setOpen={setOpenFilter} />
        </Form>
      </Formik>
      {actList.data ? (
        ((actList.data as Array<ActListVM>) || [])?.length > 0 ? (
          <>
            <TableAct
              data={actList.data as Array<ActListVM>}
              filterSelected={filterSelected}
              getActListData={getActListData}
              tableProps={tableProps}
              setTableProps={setTableProps}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ActResearchPage.displayName = 'Act Research Page component';

export default ActResearchPage;
